import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, StickyNote, ChevronLeft, ChevronRight, FolderInput, LayoutList, X } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../utils/api';
import NotesList from '../../notes/NotesList';
import NoteEditor from '../../notes/NoteEditor';
import NoteFoldersSidebar from '../../notes/NoteFoldersSidebar';
import ShareNoteModal from '../../notes/ShareNoteModal';
import toast from 'react-hot-toast';

export default function NotesPage() {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'my_notes');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [foldersOpen, setFoldersOpen] = useState(true);
  const [listOpen, setListOpen] = useState(true);
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'editor'

  const loadAll = useCallback(async (folder, search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (folder && folder !== 'unfiled') params.append('folder_id', folder);
      if (folder === 'unfiled') params.append('unfiled', 'true');
      if (search) params.append('search', search);

      const [notesRes, foldersRes, sharedRes] = await Promise.all([
        apiGet(`/notes?${params.toString()}`),
        apiGet('/note_folders'),
        apiGet('/notes/shared_with_me'),
      ]);

      if (notesRes.ok) {
        const data = await notesRes.json();
        setNotes(data.notes || []);
      }
      if (foldersRes.ok) {
        const data = await foldersRes.json();
        setFolders(data.folders || []);
      }
      if (sharedRes.ok) {
        const data = await sharedRes.json();
        setSharedNotes(data.notes || []);
      }
    } catch (err) {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshNotes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedFolder && selectedFolder !== 'unfiled') params.append('folder_id', selectedFolder);
      if (selectedFolder === 'unfiled') params.append('unfiled', 'true');
      if (searchQuery) params.append('search', searchQuery);

      const [notesRes, sharedRes, foldersRes] = await Promise.all([
        apiGet(`/notes?${params.toString()}`),
        apiGet('/notes/shared_with_me'),
        apiGet('/note_folders'),
      ]);
      if (notesRes.ok) {
        const data = await notesRes.json();
        setNotes(data.notes || []);
      }
      if (sharedRes.ok) {
        const data = await sharedRes.json();
        setSharedNotes(data.notes || []);
      }
      if (foldersRes.ok) {
        const data = await foldersRes.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      // silent refresh
    }
  }, [selectedFolder, searchQuery]);

  useEffect(() => {
    loadAll(null, '');
  }, [loadAll]);

  useEffect(() => {
    refreshNotes();
  }, [refreshNotes]);

  // Auto-open a specific note from query params (e.g., from team chat "Open in Notes")
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || loading) return;

    // Search in both own notes and shared notes
    const allNotes = [...notes, ...sharedNotes];
    const noteToOpen = allNotes.find(n => n.id === openId);
    if (noteToOpen) {
      setSelectedNote(noteToOpen);
      setShowEditor(true);
      setMobileView('editor');
      // Clear the query param so it doesn't re-open on every render
      setSearchParams({}, { replace: true });
    }
  }, [loading, notes, sharedNotes, searchParams]);

  const handleCreateNote = () => {
    setSelectedNote(null);
    setShowEditor(true);
    setMobileView('editor');
  };

  const handleSaveNote = async (noteData) => {
    try {
      let savedNote;
      if (selectedNote) {
        const response = await apiPatch(`/notes/${selectedNote.id}`, noteData);
        if (!response.ok) throw new Error('Failed to update');
        const data = await response.json();
        savedNote = data.note;
        setSelectedNote(savedNote);
        toast.success('Note updated');
      } else {
        const folderPayload = selectedFolder && selectedFolder !== 'unfiled'
          ? { ...noteData, note_folder_id: folders.find(f => f.id === selectedFolder)?.id }
          : noteData;
        const response = await apiPost('/notes', folderPayload);
        if (!response.ok) throw new Error('Failed to create');
        const data = await response.json();
        savedNote = data.note;
        setSelectedNote(savedNote);
        toast.success('Note created');
      }

      if (!noteData.title && savedNote?.id) {
        try {
          const titleResponse = await apiPost(`/notes/${savedNote.id}/auto_title`);
          if (titleResponse.ok) {
            const titleData = await titleResponse.json();
            setSelectedNote(prev => prev?.id === savedNote.id ? { ...prev, title: titleData.title } : prev);
          }
        } catch { /* optional */ }
      }

      await refreshNotes();
    } catch (err) {
      toast.error('Failed to save note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await apiDelete(`/notes/${noteId}`);
      if (!response.ok) throw new Error('Failed to delete');
      if (selectedNote?.id === noteId) {
        setSelectedNote(null);
        setShowEditor(false);
        setMobileView('list');
      }
      toast.success('Note deleted');
      await refreshNotes();
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const handleAutoTitle = async (noteId) => {
    try {
      const response = await apiPost(`/notes/${noteId}/auto_title`);
      if (!response.ok) throw new Error('Failed to generate title');
      const data = await response.json();
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, title: data.title } : n));
      if (selectedNote?.id === noteId) {
        setSelectedNote(prev => ({ ...prev, title: data.title }));
      }
      toast.success('Title generated');
    } catch (err) {
      toast.error('Failed to generate title');
    }
  };

  const handleCreateFolder = async (name, color) => {
    try {
      const response = await apiPost('/note_folders', { name, color });
      if (!response.ok) throw new Error('Failed to create folder');
      const data = await response.json();
      setFolders(prev => [...prev, data.folder]);
      toast.success('Folder created');
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      const response = await apiDelete(`/note_folders/${folderId}`);
      if (!response.ok) throw new Error('Failed to delete folder');
      setFolders(prev => prev.filter(f => f.id !== folderId));
      if (selectedFolder === folderId) setSelectedFolder(null);
      toast.success('Folder deleted');
      await refreshNotes();
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  const handleUpdateFolder = async (folderId, updates) => {
    try {
      const response = await apiPatch(`/note_folders/${folderId}`, updates);
      if (!response.ok) throw new Error('Failed to update folder');
      const data = await response.json();
      setFolders(prev => prev.map(f => (f.id === folderId ? data.folder : f)));
      toast.success('Folder updated');
    } catch (err) {
      toast.error('Failed to update folder');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const response = await apiPatch(`/notes/${note.id}`, { pinned: !note.pinned });
      if (!response.ok) throw new Error('Failed to update');
      await refreshNotes();
    } catch (err) {
      toast.error('Failed to update note');
    }
  };

  const handleMoveToFolder = async (noteId, folderId) => {
    try {
      const isShared = activeTab === 'shared' || (moveTarget && !moveTarget.is_owner);
      if (isShared) {
        // Shared note — move on the recipient's side
        const payload = folderId ? { folder_id: folderId } : { folder_id: '' };
        const response = await apiPatch(`/notes/${noteId}/move_shared`, payload);
        if (!response.ok) throw new Error('Failed to move');
      } else {
        // Own note — move the actual note
        const payload = folderId ? { note_folder_id: folderId } : { note_folder_id: '' };
        const response = await apiPatch(`/notes/${noteId}`, payload);
        if (!response.ok) throw new Error('Failed to move');
      }
      toast.success(folderId ? 'Moved to folder' : 'Moved to unfiled');
      setShowMoveModal(false);
      setMoveTarget(null);
      await refreshNotes();
    } catch (err) {
      toast.error('Failed to move note');
    }
  };

  // Shared notes are fetched unfiltered by the server; apply the sidebar folder filter
  // client-side so the recipient's folder assignment (NoteShare#note_folder_id) is honored.
  const filteredSharedNotes = !selectedFolder
    ? sharedNotes
    : selectedFolder === 'unfiled'
      ? sharedNotes.filter(n => !n.folder_id)
      : sharedNotes.filter(n => n.folder_id === selectedFolder);

  // My Notes tab shows owned notes. When the user has filed a shared note into one of
  // their own folders, that filed share should also appear when they open that folder —
  // otherwise the "move to folder" action looks broken.
  const displayNotes = (() => {
    if (activeTab === 'shared') return filteredSharedNotes;
    if (selectedFolder && selectedFolder !== 'unfiled') {
      const sharedInFolder = sharedNotes.filter(n => n.folder_id === selectedFolder);
      return [...notes, ...sharedInFolder];
    }
    return notes;
  })();

  return (
    <div className={`flex flex-col h-full overflow-hidden ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="flex flex-1 min-h-0">

      {/* Folders Sidebar — desktop: inline collapsible panel */}
      <div className={`hidden lg:flex flex-shrink-0 border-r overflow-hidden flex-col min-h-0 transition-all duration-300 ease-in-out ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      } ${foldersOpen ? 'w-48' : 'w-0 border-r-0'}`}>
        <NoteFoldersSidebar
          folders={folders}
          selectedFolder={selectedFolder}
          onSelectFolder={(id) => { setSelectedFolder(id); setActiveTab('my_notes'); }}
          onCreateFolder={handleCreateFolder}
          onDeleteFolder={handleDeleteFolder}
          onUpdateFolder={handleUpdateFolder}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sharedCount={sharedNotes.length}
          theme={theme}
        />
      </div>

      {/* Folders Sidebar — mobile/tablet: slide-over drawer */}
      {mobileFoldersOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFoldersOpen(false)} />
          <div className={`relative w-64 max-w-[80%] h-full shadow-xl flex flex-col ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="font-rasa font-semibold text-sm">Folders</h3>
              <button
                onClick={() => setMobileFoldersOpen(false)}
                className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NoteFoldersSidebar
                folders={folders}
                selectedFolder={selectedFolder}
                onSelectFolder={(id) => { setSelectedFolder(id); setActiveTab('my_notes'); setMobileFoldersOpen(false); }}
                onCreateFolder={handleCreateFolder}
                onDeleteFolder={handleDeleteFolder}
                onUpdateFolder={handleUpdateFolder}
                activeTab={activeTab}
                onTabChange={(tab) => { setActiveTab(tab); setMobileFoldersOpen(false); }}
                sharedCount={sharedNotes.length}
                theme={theme}
              />
            </div>
          </div>
        </div>
      )}

      {/* Notes List — full width on mobile when no editor, collapsible on desktop */}
      <div className={`flex-shrink-0 border-r overflow-hidden flex flex-col min-h-0 transition-all duration-300 ease-in-out ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      } ${
        mobileView === 'editor' ? 'hidden md:flex' : 'flex w-full md:w-auto'
      } ${listOpen ? 'md:w-72' : 'md:w-0 md:border-r-0'}`}>
        {/* List Header */}
        <div className={`flex-shrink-0 flex items-center justify-between px-4 py-3 pt-4 md:pt-3 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-1.5 min-w-0 ml-8 md:ml-0">
            {/* Mobile: open folders drawer */}
            <button
              onClick={() => setMobileFoldersOpen(true)}
              className={`lg:hidden flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-rasa transition-colors ${
                theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }`}
            >
              <LayoutList size={13} />
              Folders
            </button>
            {/* Desktop: toggle folders panel */}
            <button
              onClick={() => setFoldersOpen(!foldersOpen)}
              className={`hidden lg:block p-1 rounded transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
              title={foldersOpen ? 'Hide folders' : 'Show folders'}
            >
              {foldersOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
            <h2 className="text-sm font-rasa font-semibold truncate">
              {activeTab === 'shared' ? 'Shared' : 'Notes'}
              <sup className={`ml-0.5 text-[9px] font-normal ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {displayNotes.length}
              </sup>
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Tab toggle — visible on mobile and medium screens (where folder sidebar is hidden) */}
            <div className="flex lg:hidden gap-1">
              <button
                onClick={() => { setActiveTab('my_notes'); setSelectedFolder(null); }}
                className={`px-2 py-1 text-[10px] font-rasa rounded ${activeTab === 'my_notes' ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}
              >
                Mine
              </button>
              <button
                onClick={() => setActiveTab('shared')}
                className={`px-2 py-1 text-[10px] font-rasa rounded ${activeTab === 'shared' ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}
              >
                Shared {sharedNotes.length > 0 && `(${sharedNotes.length})`}
              </button>
            </div>
            {activeTab === 'my_notes' && (
              <button
                onClick={handleCreateNote}
                className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                title="New note"
              >
                <Plus size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className={`flex-shrink-0 px-3 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}>
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`bg-transparent outline-none text-xs w-full font-rasa ${theme === 'dark' ? 'placeholder-gray-500' : 'placeholder-gray-400'}`}
            />
          </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto">
          <NotesList
            notes={displayNotes}
            selectedNote={selectedNote}
            onSelectNote={(note) => {
              setSelectedNote(note);
              setShowEditor(true);
              setMobileView('editor');
            }}
            onDeleteNote={handleDeleteNote}
            onTogglePin={handleTogglePin}
            onShareNote={(note) => { setSelectedNote(note); setShowShareModal(true); }}
            onMoveNote={(note) => { setMoveTarget(note); setShowMoveModal(true); }}
            loading={loading}
            theme={theme}
            isSharedView={activeTab === 'shared'}
          />
        </div>
      </div>

      {/* Main Editor Area — full width on mobile when editing, flex-1 on desktop */}
      <div className={`flex-1 flex flex-col min-w-0 md:min-w-[320px] min-h-0 overflow-hidden relative ${
        mobileView === 'list' ? 'hidden md:flex' : 'flex'
      }`}>
        {!listOpen && (
          <button
            onClick={() => setListOpen(true)}
            className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-r-lg shadow-md ${
              theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-white hover:bg-gray-50 text-gray-500 border border-l-0 border-gray-200'
            }`}
            title="Show notes list"
          >
            <ChevronRight size={14} />
          </button>
        )}

        {showEditor ? (
          <NoteEditor
            note={selectedNote}
            onSave={handleSaveNote}
            onNoteCreated={(note) => setSelectedNote(note)}
            onClose={() => {
              setShowEditor(false);
              setSelectedNote(null);
              setMobileView('list');
            }}
            onAutoTitle={handleAutoTitle}
            onCollapseList={() => setListOpen(!listOpen)}
            listOpen={listOpen}
            onBack={() => setMobileView('list')}
            theme={theme}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <StickyNote size={48} className="mx-auto mb-4 opacity-20" />
              <p className={`font-rasa text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                Select a note or create a new one
              </p>
              <button
                onClick={handleCreateNote}
                className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-rasa"
              >
                <Plus size={16} />
                New Note
              </button>
            </div>
          </div>
        )}
      </div>

      </div>

      {/* Share Modal */}
      {showShareModal && selectedNote && (
        <ShareNoteModal
          note={selectedNote}
          onClose={() => setShowShareModal(false)}
          onShared={() => { refreshNotes(); setShowShareModal(false); }}
          theme={theme}
        />
      )}

      {/* Move to Folder Modal */}
      {showMoveModal && moveTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowMoveModal(false)}>
          <div
            className={`w-full max-w-xs rounded-xl shadow-xl p-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-4">
              <FolderInput size={18} />
              <h3 className="font-dm-serif font-semibold">Move to Folder</h3>
            </div>
            <div className="space-y-1">
              <button
                onClick={() => handleMoveToFolder(moveTarget.id, null)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
                  !moveTarget.folder_id
                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                    : theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}
              >
                Unfiled
              </button>
              {folders.map(f => (
                <button
                  key={f.id}
                  onClick={() => handleMoveToFolder(moveTarget.id, f.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-rasa flex items-center gap-2 transition-colors ${
                    moveTarget.folder_id === f.id
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                      : theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: f.color }} />
                  {f.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowMoveModal(false)}
              className={`mt-3 w-full py-2 text-sm font-rasa rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
